import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';
import { ChevronLeft, ChevronDown, ChevronUp, Play, Dumbbell } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import type { ProgramDay } from '@/lib/programBuilder';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface AssignedProgram {
  programId: string;
  programName: string;
  coachName: string;
  days: ProgramDay[];
}

export default function MyProgramScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const c = theme.colors;

  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<AssignedProgram | null>(null);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());

  const todayIndex = new Date().getDay();

  const loadProgram = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const { data: assignments } = await supabase
        .from('program_assignments')
        .select('program_id, coach_id')
        .eq('client_id', profile.id)
        .eq('status', 'active')
        .limit(1);

      if (!assignments || assignments.length === 0) { setProgram(null); setLoading(false); return; }

      const assignment = assignments[0];
      const { data: prog } = await supabase
        .from('coach_programs')
        .select('id, name, days, coach_id')
        .eq('id', assignment.program_id)
        .single();

      if (!prog) { setProgram(null); setLoading(false); return; }

      let coachName = 'Your Coach';
      if (prog.coach_id) {
        const { data: cp } = await supabase.from('profiles').select('display_name, handle').eq('id', prog.coach_id).single();
        if (cp) coachName = cp.display_name || cp.handle || 'Your Coach';
      }

      setProgram({ programId: prog.id, programName: prog.name, coachName, days: (prog.days ?? []) as ProgramDay[] });
      // Auto-expand today
      setExpandedDays(new Set([todayIndex % ((prog.days as ProgramDay[])?.length || 1)]));
    } catch (err) {
      console.warn('[MyProgram] load error:', err);
      setProgram(null);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, todayIndex]);

  useEffect(() => { loadProgram(); }, [loadProgram]);

  const toggleDay = (idx: number) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleLoadDay = (dayIndex: number) => {
    if (!program) return;
    router.push({
      pathname: '/(tabs)/record',
      params: { programId: program.programId, programDayIndex: String(dayIndex), view: 'log' },
    } as any);
  };

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
        <View style={styles.center}><ActivityIndicator size="large" color={c.accent} /></View>
      </SafeAreaView>
    );
  }

  if (!program) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => router.back()}><ChevronLeft size={24} color={c.text} /></TouchableOpacity>
          <Text style={[styles.headerTitle, { color: c.text }]}>My Program</Text>
          <View style={{ width: 24 }} />
        </View>
        <View style={styles.center}>
          <Dumbbell size={48} color={c.textTertiary} />
          <Text style={[styles.emptyTitle, { color: c.text }]}>No program assigned yet</Text>
          <Text style={[styles.emptySubtitle, { color: c.textSecondary }]}>No program yet — browse templates to get started</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }}>
      <View style={[styles.headerRow, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()}><ChevronLeft size={24} color={c.text} /></TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>My Program</Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Text style={[styles.programName, { color: c.text }]}>{program.programName}</Text>
        <Text style={[styles.coachName, { color: c.textSecondary }]}>Coach: {program.coachName}</Text>

        {/* Week view */}
        <View style={[styles.weekRow, { borderColor: c.border }]}>
          {program.days.map((day, idx) => {
            const isToday = idx === todayIndex % program.days.length;
            const hasExercises = day.exercises.length > 0;
            return (
              <TouchableOpacity
                key={day.id}
                style={[
                  styles.weekDayCell,
                  { backgroundColor: isToday ? c.accent : hasExercises ? c.surface : 'transparent', borderColor: c.border },
                ]}
                onPress={() => toggleDay(idx)}>
                <Text style={[styles.weekDayLabel, { color: isToday ? c.accentForeground : hasExercises ? c.text : c.textTertiary }]}>
                  {day.name.slice(0, 3)}
                </Text>
                <Text style={[styles.weekDayCount, { color: isToday ? c.accentForeground : c.textSecondary }]}>
                  {hasExercises ? `${day.exercises.length}` : '—'}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Expandable day cards */}
        {program.days.map((day, idx) => {
          const isExpanded = expandedDays.has(idx);
          const isToday = idx === todayIndex % program.days.length;
          return (
            <View key={day.id} style={[styles.dayCard, { backgroundColor: c.surface, borderColor: isToday ? c.accent : c.border }]}>
              <TouchableOpacity style={styles.dayCardHeader} onPress={() => toggleDay(idx)}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.dayCardTitle, { color: c.text }]}>
                    {day.name}{isToday ? ' (Today)' : ''}
                  </Text>
                  <Text style={[styles.dayCardSub, { color: c.textSecondary }]}>
                    {day.exercises.length === 0 ? 'Rest day' : `${day.exercises.length} exercises`}
                  </Text>
                </View>
                {isExpanded ? <ChevronUp size={20} color={c.textSecondary} /> : <ChevronDown size={20} color={c.textSecondary} />}
              </TouchableOpacity>

              {isExpanded && day.exercises.length > 0 && (
                <View style={[styles.dayCardBody, { borderTopColor: c.border }]}>
                  {day.exercises.map((ex) => (
                    <View key={ex.id} style={[styles.exRow, { borderBottomColor: c.border }]}>
                      <Text style={[styles.exName, { color: c.text }]}>{ex.exerciseName}</Text>
                      <Text style={[styles.exDetail, { color: c.textSecondary }]}>
                        {ex.targetSets}×{ex.targetReps}
                        {ex.targetWeight ? ` @ ${ex.targetWeight}` : ''}
                        {ex.rpe ? ` RPE ${ex.rpe}` : ''}
                      </Text>
                      {ex.notes ? <Text style={[styles.exNotes, { color: c.textTertiary }]}>{ex.notes}</Text> : null}
                    </View>
                  ))}
                  <TouchableOpacity style={[styles.loadDayBtn, { backgroundColor: c.accent }]} onPress={() => handleLoadDay(idx)}>
                    <Play size={16} color={c.accentForeground} />
                    <Text style={[styles.loadDayBtnText, { color: c.accentForeground }]}>Load into Workout</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 12 },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  headerTitle: { fontSize: 18, fontFamily: TYPO.heading.bold },
  scrollContent: { padding: 16, paddingBottom: 40 },
  programName: { fontSize: 24, fontFamily: TYPO.heading.bold },
  coachName: { fontSize: 14, fontFamily: TYPO.body.regular, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontFamily: TYPO.heading.bold },
  emptySubtitle: { fontSize: 14, fontFamily: TYPO.body.regular },
  weekRow: { flexDirection: 'row', gap: 6, marginBottom: 20 },
  weekDayCell: { flex: 1, alignItems: 'center', paddingVertical: 10, borderRadius: 2, borderWidth: 1 },
  weekDayLabel: { fontSize: 11, fontWeight: '700' },
  weekDayCount: { fontSize: 10, marginTop: 2 },
  dayCard: { borderWidth: 1, borderRadius: 2, marginBottom: 12, overflow: 'hidden' },
  dayCardHeader: { flexDirection: 'row', alignItems: 'center', padding: 14 },
  dayCardTitle: { fontSize: 16, fontFamily: TYPO.heading.semibold },
  dayCardSub: { fontSize: 12, fontFamily: TYPO.body.regular, marginTop: 2 },
  dayCardBody: { borderTopWidth: 1, paddingHorizontal: 14, paddingTop: 8, paddingBottom: 14 },
  exRow: { paddingVertical: 8, borderBottomWidth: StyleSheet.hairlineWidth },
  exName: { fontSize: 14, fontWeight: '600' },
  exDetail: { fontSize: 12, marginTop: 2 },
  exNotes: { fontSize: 11, fontStyle: 'italic', marginTop: 2 },
  loadDayBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 2, marginTop: 10 },
  loadDayBtnText: { fontSize: 14, fontWeight: '700' },
});
